import type { ProductFamily } from "./productWorkflow";

/**
 * Ссылки с сайта Egel в конструктор (12.10): /konstruktor?start=shelving —
 * открыть раздел, &new=1 — с пустым проектом, ?model=<id> — модель витрины.
 */
export interface SiteLink { start?: ProductFamily; fresh: boolean; model?: string; /** Сразу открыть «Отправить дизайнеру» (кнопка на странице модели). */ order?: boolean }
const FAMILIES: ProductFamily[] = ["wardrobe", "shelving", "chest"];
const PARAMS = ["start", "new", "model", "order"];

export function readSiteLink(search: string): SiteLink | null {
  const params = new URLSearchParams(search);
  const start = params.get("start");
  const model = params.get("model")?.trim();
  const link: SiteLink = {
    start: FAMILIES.includes(start as ProductFamily) ? start as ProductFamily : undefined,
    fresh: params.get("new") === "1",
    model: model && /^[A-Za-z0-9_:.-]{1,120}$/.test(model) ? model : undefined,
    ...(params.get("order") === "1" ? { order: true } : {})
  };
  return link.start || link.model ? link : null;
}

/** Адрес без параметров ссылки: после перезагрузки страница не открывает модель заново. */
export function withoutSiteLink(href: string) {
  const url = new URL(href);
  PARAMS.forEach((name) => url.searchParams.delete(name));
  return `${url.pathname}${url.search}${url.hash}`;
}

/** Конструктор открыт на адресе сайта — сервер добавляет window.EGEL_SITE. */
export const siteHome = (): string | null => {
  const site = (globalThis as { EGEL_SITE?: { home?: unknown } }).EGEL_SITE;
  return typeof site?.home === "string" ? site.home : null;
};

/**
 * Пришли с сайта за конкретным изделием (приёмка 22.10): в конструкторе нет
 * выбора «шкаф / стеллаж / комод» — «назад» ведёт в раздел сайта. Помним в
 * пределах вкладки, чтобы перезагрузка страницы не возвращала выбор.
 */
const LOCK_KEY = "egel-site-family";
const FAMILY_PATHS: Record<ProductFamily, string> = { wardrobe: "shkafy", shelving: "stellazhi", chest: "komody" };
export const SITE_FAMILY_BACK_LABELS: Record<ProductFamily, string> = {
  wardrobe: "Шкафы на сайте", shelving: "Стеллажи на сайте", chest: "Комоды на сайте"
};

export function readSiteFamilyLock(openedFromSiteLink: boolean): ProductFamily | null {
  if (!siteHome()) return null;
  try {
    const navigation = globalThis.performance?.getEntriesByType?.("navigation")[0] as PerformanceNavigationTiming | undefined;
    // Конструктор открыли с сайта простой ссылкой — выбор изделия снова есть.
    if (!openedFromSiteLink && navigation?.type !== "reload" && navigation?.type !== "back_forward") {
      globalThis.sessionStorage?.removeItem(LOCK_KEY);
      return null;
    }
    const value = globalThis.sessionStorage?.getItem(LOCK_KEY);
    return FAMILIES.includes(value as ProductFamily) ? value as ProductFamily : null;
  } catch {
    return null;
  }
}

export function storeSiteFamilyLock(family: ProductFamily) {
  try { globalThis.sessionStorage?.setItem(LOCK_KEY, family); } catch { /* приватный режим — без памяти */ }
}

/** Раздел сайта для изделия: /shkafy, /stellazhi, /komody. */
export function siteFamilyUrl(family: ProductFamily) {
  const home = siteHome();
  if (!home) return null;
  try { return new URL(FAMILY_PATHS[family], new URL(home, globalThis.location?.href ?? "http://localhost/")).href; }
  catch { return null; }
}
